import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// post-client-mod -- 分类管理器（管理员使用）
// ============================================================================
import * as React from 'react';
import { getPostApiClient } from '../post-api-client.js';
import { useCategories } from '../hooks/use-categories.js';
/**
 * 分类管理组件
 */
export function CategoryManager({ onUpdated }) {
    const { categories, loading, error, refresh } = useCategories();
    const [name, setName] = React.useState('');
    const [slug, setSlug] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [deletingSlug, setDeletingSlug] = React.useState(null);
    const [localError, setLocalError] = React.useState(null);
    const [successMsg, setSuccessMsg] = React.useState(null);
    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim())
            return;
        setSubmitting(true);
        setLocalError(null);
        setSuccessMsg(null);
        try {
            await getPostApiClient().createCategory({
                name: name.trim(),
                slug: slug.trim() || undefined,
            });
            setName('');
            setSlug('');
            setSuccessMsg('分类创建成功');
            refresh();
            onUpdated?.();
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : '创建分类失败');
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleDelete = async (targetSlug) => {
        if (!confirm(`确定要删除分类 "${targetSlug}" 吗？相关文章的分类将被清空。`))
            return;
        setDeletingSlug(targetSlug);
        setLocalError(null);
        setSuccessMsg(null);
        try {
            await getPostApiClient().deleteCategory(targetSlug);
            setSuccessMsg(`分类 "${targetSlug}" 已删除`);
            refresh();
            onUpdated?.();
        }
        catch (err) {
            setLocalError(err instanceof Error ? err.message : '删除分类失败');
        }
        finally {
            setDeletingSlug(null);
        }
    };
    return (_jsxs("div", { style: styles.container, children: [_jsx("h3", { style: styles.title, children: "\u5206\u7C7B\u7BA1\u7406" }), _jsx("form", { onSubmit: handleCreate, style: styles.form, children: _jsxs("div", { style: styles.formRow, children: [_jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "\u5206\u7C7B\u540D\u79F0", required: true, style: styles.input }), _jsx("input", { type: "text", value: slug, onChange: (e) => setSlug(e.target.value), placeholder: "slug\uFF08\u53EF\u9009\uFF09", style: styles.input }), _jsx("button", { type: "submit", disabled: submitting || !name.trim(), style: styles.createButton, children: submitting ? '创建中...' : '创建' })] }) }), localError && _jsx("div", { style: styles.error, children: localError }), successMsg && _jsx("div", { style: styles.success, children: successMsg }), loading ? (_jsx("div", { style: styles.loading, children: "\u52A0\u8F7D\u4E2D..." })) : error ? (_jsx("div", { style: styles.error, children: error })) : categories.length === 0 ? (_jsx("p", { style: styles.emptyText, children: "\u6682\u65E0\u5206\u7C7B" })) : (_jsx("div", { style: styles.list, children: categories.map(cat => (_jsxs("div", { style: styles.listItem, children: [_jsxs("div", { style: styles.itemInfo, children: [_jsx("span", { style: styles.itemName, children: cat.name }), _jsx("span", { style: styles.itemSlug, children: cat.slug }), _jsxs("span", { style: styles.itemCount, children: [cat.postCount, " \u7BC7\u6587\u7AE0"] })] }), _jsx("button", { onClick: () => handleDelete(cat.slug), disabled: deletingSlug === cat.slug, style: styles.deleteButton, children: deletingSlug === cat.slug ? '删除中...' : '删除' })] }, cat.slug))) }))] }));
}
const styles = {
    container: {
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    title: {
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: 600,
        color: '#1e293b',
    },
    form: {
        marginBottom: '16px',
    },
    formRow: {
        display: 'flex',
        gap: '8px',
    },
    input: {
        flex: 1,
        padding: '8px 12px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '13px',
        outline: 'none',
    },
    createButton: {
        padding: '8px 16px',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        border: '1px solid #f3f4f6',
    },
    itemInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flex: 1,
        minWidth: 0,
    },
    itemName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1e293b',
    },
    itemSlug: {
        fontSize: '12px',
        color: '#9ca3af',
        fontFamily: 'monospace',
    },
    itemCount: {
        fontSize: '12px',
        color: '#6366f1',
        whiteSpace: 'nowrap',
    },
    deleteButton: {
        padding: '4px 10px',
        border: '1px solid #fecaca',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        color: '#ef4444',
        fontSize: '12px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    error: {
        padding: '8px 12px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#dc2626',
        marginBottom: '12px',
    },
    success: {
        padding: '8px 12px',
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '6px',
        fontSize: '13px',
        color: '#166534',
        marginBottom: '12px',
    },
    loading: {
        padding: '20px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '13px',
    },
    emptyText: {
        margin: 0,
        padding: '20px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '13px',
    },
};
//# sourceMappingURL=category-manager.js.map